import { useEffect, useRef, useState } from 'react'
import { Search, X, Clock } from 'lucide-react'
import { cn } from '../atoms'
import {
  SEARCH_OPERATORS, getRecentSearches, addRecentSearch, clearRecentSearches, normalizeQuery,
} from '../../utils/searchQuery'

export type SearchMode = 'gmail' | 'simple'

interface SearchBarProps {
  value: string
  onChange: (v: string) => void
  onSubmit?: (v: string) => void
  placeholder?: string
  mode?: SearchMode
  suggestions?: Array<{ label: string; value: string }>
  className?: string
  ariaLabel?: string
}

export function SearchBar({
  value,
  onChange,
  onSubmit,
  placeholder = 'Buscar…',
  mode = 'gmail',
  suggestions,
  className,
  ariaLabel = 'Buscar',
}: SearchBarProps) {
  const [isFocused, setIsFocused] = useState(false)
  const [recent, setRecent] = useState<string[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setRecent(getRecentSearches())
  }, [])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === '/' && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
        e.preventDefault()
        inputRef.current?.focus()
      }
      if (e.key === 'Escape' && document.activeElement === inputRef.current) {
        inputRef.current?.blur()
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsFocused(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Inline suggestions: operator autocomplete as you type
  const lastToken = value.split(/\s+/).pop() || ''
  const isTypingOperator = mode === 'gmail' && /^[a-z_]+$/i.test(lastToken) && lastToken.length >= 1
  const typedBase = lastToken.toLowerCase()

  const operatorSuggestions = isTypingOperator
    ? SEARCH_OPERATORS.filter(op => {
        const base = op.op.split(':')[0].toLowerCase()
        return base.startsWith(typedBase) && base !== typedBase
      }).slice(0, 5)
    : []

  const showDropdown = isFocused && (operatorSuggestions.length > 0 || recent.length > 0 || suggestions)

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (value.trim()) {
        addRecentSearch(value)
        setRecent(getRecentSearches())
      }
      onSubmit?.(value)
      setIsFocused(false)
      inputRef.current?.blur()
    }
  }

  const applyOperator = (op: string) => {
    const tokens = value.split(/\s+/).filter(Boolean)
    const last = tokens[tokens.length - 1] || ''
    const trailingSpace = op.endsWith(':') ? '' : ' '

    // Replace partial operator or append
    if (/^[a-z_]+$/i.test(last)) {
      tokens[tokens.length - 1] = op + trailingSpace
    } else {
      const base = value.replace(/\s+$/, '')
      const sep = base.length > 0 ? ' ' : ''
      onChange(base + sep + op + trailingSpace)
      return
    }
    onChange(tokens.join(' ') + (trailingSpace ? ' ' : ''))
  }

  const applyRecent = (r: string) => {
    onChange(r)
    inputRef.current?.focus()
  }

  const clearAll = () => {
    onChange('')
    inputRef.current?.focus()
  }

  return (
    <div ref={containerRef} className={cn('relative flex-1', className)}>
      <div
        className={cn(
          'flex items-center gap-2 px-3 h-10 border rounded-lg bg-white dark:bg-gray-800 transition-all',
          isFocused
            ? 'border-primary-400 dark:border-primary-500 shadow-sm ring-2 ring-primary-100 dark:ring-primary-900/30'
            : 'border-gray-300 dark:border-gray-600 shadow-sm hover:shadow',
        )}
      >
        <Search className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => {
            const normalized = normalizeQuery(e.target.value)
            onChange(normalized)
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setTimeout(() => setIsFocused(false), 150)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-label={ariaLabel}
          className="flex-1 bg-transparent text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none"
        />
        {value && (
          <button
            type="button"
            onClick={clearAll}
            className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400"
            aria-label="Limpiar búsqueda"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Atajo "/" */}
      {!isFocused && !value && (
        <kbd className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded pointer-events-none">
          /
        </kbd>
      )}

      {/* Dropdown inline */}
      {showDropdown && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 max-h-72 overflow-y-auto">
          {/* Operator autocomplete */}
          {operatorSuggestions.length > 0 && (
            <div className="p-1">
              {operatorSuggestions.map(op => (
                <button
                  key={op.op}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    applyOperator(op.op)
                    requestAnimationFrame(() => {
                      inputRef.current?.focus()
                      const len = inputRef.current?.value.length || 0
                      inputRef.current?.setSelectionRange(len, len)
                    })
                  }}
                  className="flex items-center gap-2.5 w-full px-2.5 py-1.5 text-left text-sm rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <span className="text-xs font-mono text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 px-1.5 py-0.5 rounded">
                    {op.op}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{op.desc}</span>
                </button>
              ))}
            </div>
          )}

          {/* Recent searches */}
          {recent.length > 0 && (
            <div className={cn('p-1', operatorSuggestions.length > 0 && 'border-t border-gray-100 dark:border-gray-800')}>
              <div className="flex items-center justify-between px-2.5 py-1">
                <span className="text-[10px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Recientes
                </span>
                <button
                  onMouseDown={(e) => {
                    e.preventDefault()
                    clearRecentSearches()
                    setRecent([])
                  }}
                  className="text-[10px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  Limpiar
                </button>
              </div>
              {recent.slice(0, 5).map(r => (
                <button
                  key={r}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    applyRecent(r)
                  }}
                  className="flex items-center gap-2.5 w-full px-2.5 py-1.5 text-left text-sm text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <Clock className="w-3 h-3 text-gray-400 flex-shrink-0" />
                  <span className="truncate">{r}</span>
                </button>
              ))}
            </div>
          )}

          {/* Static suggestions (simple mode) */}
          {mode === 'simple' && suggestions && suggestions.length > 0 && (
            <div className="p-1">
              {suggestions.slice(0, 5).map(s => (
                <button
                  key={s.value}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    onChange(s.value)
                    inputRef.current?.focus()
                  }}
                  className="flex items-center gap-2.5 w-full px-2.5 py-1.5 text-left text-sm text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <Search className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span className="truncate">{s.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
